// Contrail V3.9.1 — rotating top-down markers. Shared by the main map, phone map, Follow windows, share page,
// history playback and the Icon & tail preview.
//  - The silhouette <svg> rotates to the heading; the label and the click target never rotate.
//  - Below 3 km/h (or without a course) the last good heading is kept, so parked cars and boats at anchor don't spin.
//    The first heading we see is accepted at any speed (Traccar keeps the last course while stopped), else 0° (north).
//  - Turns take the short way round (350° → 10° = +20°). CSS animates it (~300 ms; off under reduced motion).
//  - Markers are updated in place — never re-created on each position — so the turn can animate.
import { normIcon, rotates, silhouetteSvg } from "./vehicleIcons";

type RotState = { icon: string; heading: number | null; shown: number };
const states = new WeakMap<HTMLElement, RotState>();

const validCourse = (c: unknown) => c != null && c !== "" && Number.isFinite(Number(c));
const norm360 = (d: number) => ((d % 360) + 360) % 360;

/** Next displayed angle from `cur` to `target`, turning the short way (keeps the running total so CSS animates correctly). */
export function shortestTurn(cur: number, target: number) {
  const delta = ((norm360(target) - norm360(cur) + 540) % 360) - 180;
  return cur + delta;
}

/** Heading to show, given the previous kept heading, the new course and speed. */
export function keptHeading(prev: number | null, course: unknown, kmh: number | null | undefined) {
  if (!validCourse(course)) return prev;
  if (prev == null) return norm360(Number(course));              // first heading: accept at any speed
  if (kmh != null && kmh < 3) return prev;                       // slow / parked: hold
  return norm360(Number(course));
}

/** Put (or update) the silhouette inside `dot`. Returns the heading now shown (0–359) or null if unknown. */
export function setSilhouette(dot: HTMLElement, icon: string | null | undefined, course: unknown, kmh: number | null | undefined, size = 36) {
  const ic = normIcon(icon);
  let s = states.get(dot);
  let fresh = false;
  if (!s || s.icon !== ic || !dot.querySelector("svg.sil")) {
    dot.innerHTML = silhouetteSvg(ic, size);
    s = { icon: ic, heading: s?.heading ?? null, shown: s?.shown ?? 0 };
    states.set(dot, s);
    fresh = true;
  }
  s.heading = keptHeading(s.heading, course, kmh);
  const target = rotates(ic) ? (s.heading ?? 0) : 0;
  s.shown = fresh ? target : shortestTurn(s.shown, target);
  const svg = dot.querySelector<SVGElement>("svg.sil")!;
  svg.style.transform = `rotate(${s.shown}deg)`;
  dot.dataset.heading = String(Math.round(norm360(s.shown)));
  return s.heading;
}

/** Make sure a marker element has its .mk-dot (and optionally .mk-label) children; returns them. */
export function markerParts(el: HTMLElement, withLabel = true) {
  let dot = el.querySelector<HTMLElement>(":scope > .mk-dot");
  if (!dot) { dot = document.createElement("span"); dot.className = "mk-dot"; el.appendChild(dot); }
  let label = el.querySelector<HTMLElement>(":scope > .mk-label");
  if (withLabel && !label) { label = document.createElement("span"); label.className = "mk-label"; el.appendChild(label); }
  if (!withLabel && label) { label.remove(); label = null; }
  return { dot, label };
}

/** Offset for a left-anchored marker so the silhouette's centre sits on the position. */
export const MK_OFFSET: [number, number] = [-18, 0];
